/**
 * Single source of truth for per-page SEO + social-share meta tags.
 * Every top-level page renders `<PageSeo />` instead of hand-rolling
 * a `<Head>` block, so the og:image / twitter:image / canonical /
 * description shape stays consistent and we can change it in one
 * place (e.g. flipping the OG endpoint, adding a new social tag).
 *
 * Brand-level defaults — twitter:card, twitter:site, twitter:creator,
 * og:type, og:site_name — live in _document.tsx and apply to every
 * route, so we don't repeat them here.
 *
 * The stock page (/s/[exchange]/[ticker]) intentionally rolls its
 * own `<Head>` because it needs the dynamic `buildStockOgImage`
 * (ticker pill + company logo) and an absolute canonical built from
 * route params — different enough that wedging it into this
 * component costs more than it saves.
 */
import Head from "next/head";
import { buildPageOgImage } from "@/lib/page-og";
import { BreadcrumbJsonLd } from "@/components/json-ld/JsonLd";

const SITE_ORIGIN = "https://app.ortex.com";

export interface PageSeoProps {
    /** Full SEO title — used as `<title>`, og:title, twitter:title (e.g. "Short Interest - ORTEX"). */
    title: string;
    /** Short page name rendered on the OG card eyebrow + image alt (e.g. "Short Interest"). */
    pageName: string;
    /** Site-relative path used for canonical, og:url, twitter:url (e.g. "/short-interest"). */
    canonicalPath: string;
    /**
     * Absolute canonical on another ORTEX site, overriding `canonicalPath`.
     * For content another property owns, e.g. articles, which ortex.news owns.
     */
    canonicalUrl?: string;
    description?: string;
    keywords?: string;
    /**
     * Set true to suppress the page from search indexes. Used by
     * internal routes (/idle, /coming-soon, /admin/*) and any page
     * whose content shouldn't appear in Google / AI-overview results.
     * The site-wide "index,follow,..." robots meta lives in _app's
     * next/head with the same key, so this one replaces it rather than
     * shipping alongside it (two robots metas is a conflicting signal).
     *
     * `true` is "noindex,nofollow". Pass "follow" for a page that should
     * stay out of the index but whose links crawlers should still follow,
     * e.g. a listing that is the only on-site path to its articles.
     */
    noindex?: boolean | "follow";
    /**
     * Breadcrumb trail under the homepage, e.g.
     * `[{ name: "Short Interest", path: "/short-interest" }]` →
     * emitted as a BreadcrumbList JSON-LD. Pass an empty array (or
     * omit) on the homepage. The "ORTEX" root entry is added
     * automatically by BreadcrumbJsonLd.
     */
    breadcrumbs?: { name: string; path: string }[];
}

export default function PageSeo({
    title,
    pageName,
    canonicalPath,
    canonicalUrl,
    description,
    keywords,
    noindex,
    breadcrumbs,
}: PageSeoProps) {
    const url = canonicalUrl || `${SITE_ORIGIN}${canonicalPath}`;
    const { ogImageUrl, cardTitle } = buildPageOgImage(pageName);
    return (
        <>
            <Head>
                <title>{title}</title>
                <link rel="canonical" href={url} />
                {description && <meta name="description" content={description} />}
                {keywords && <meta name="keywords" content={keywords} />}
                {noindex && <meta name="robots" content={noindex === "follow" ? "noindex,follow" : "noindex,nofollow"} key="robots" />}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:url" content={url} />
                {description && <meta name="twitter:description" content={description} />}
                <meta name="twitter:image" content={ogImageUrl} />
                <meta name="twitter:image:alt" content={cardTitle} />
                <meta property="og:title" content={title} />
                <meta property="og:url" content={url} />
                {description && <meta property="og:description" content={description} />}
                <meta property="og:image" content={ogImageUrl} key="og:image" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content={cardTitle} />
            </Head>
            {breadcrumbs && breadcrumbs.length > 0 && <BreadcrumbJsonLd trail={breadcrumbs} />}
        </>
    );
}
